import { container, singleton } from 'tsyringe';
import { ClassOf } from '../types/class-of.type';
import { PersistenceConfig } from './persistence.config';
import { Repository } from './repository';

/**
 * Singleton that manages preloading of registered repositories after user login.
 * Repositories register themselves via registerForPreload(), then the app
 * bootstrap calls preloadAll() once the user is authenticated.
 */
@singleton()
export class PersistencePreloader {

    private _repositories: ClassOf<Repository<any>>[] = [];

    /**
     * Registers a repository class to be included in the next preloadAll() call.
     */
    public registerForPreload(repositoryClass: ClassOf<Repository<any>>): void {
        this._repositories.push(repositoryClass);
    }

    /**
     * Starts preloading all registered repositories.
     * No-op when PersistenceConfig.isLoggedIn() is false.
     * Uses Promise.allSettled so a failure in one repository does not block others.
     */
    public preloadAll(): void {
        if (!PersistenceConfig.isLoggedIn()) {
            return;
        }

        const tasks: Promise<void>[] = this._repositories.map(repositoryClass =>
            Promise.resolve().then(() => {
                const repository = container.resolve(repositoryClass);
                repository.startPreload();
            })
        );

        Promise.allSettled(tasks).then(results => {
            results.forEach((result, index) => {
                if (result.status === 'rejected') {
                    console.error(
                        `PersistencePreloader: startPreload failed for ${this._repositories[index]?.name ?? String(index)}`,
                        (result as PromiseRejectedResult).reason
                    );
                }
            });
        });
    }
}
